import React from "react";
import { ArrowRight, Briefcase, Award, GitBranch, TrendingUp, Heart } from "lucide-react";

const MOVE_META = {
  new_role: { label: "New role", icon: Briefcase },
  promotion: { label: "Promotion", icon: TrendingUp },
  board_appointment: { label: "Board appointment", icon: Award },
  transition: { label: "Transition", icon: GitBranch },
};

export default function CareerMoveCard({ move, industryLabel }) {
  const meta = MOVE_META[move.move_type] || MOVE_META.new_role;
  const Icon = meta.icon;
  return (
    <article
      className="rounded-lg border border-border bg-white p-5 md:p-6 hover:border-primary/60 transition-colors"
      data-testid={`career-move-${move.id}`}
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-full bg-primary/8 text-primary flex items-center justify-center font-serif text-lg shrink-0" aria-hidden>
          {initials(move.leader_name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-medium text-primary truncate">{move.leader_name}</div>
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              <Icon size={11} /> {meta.label}
            </span>
            {industryLabel && (
              <span className="rounded-full bg-accent/15 text-accent px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold">
                {industryLabel}
              </span>
            )}
          </div>

          <div className="mt-2 text-sm text-muted-foreground">
            {move.from_title && move.from_organization && (
              <>
                <span className="line-through opacity-70">
                  {move.from_title} · {move.from_organization}
                </span>{" "}
                <ArrowRight size={12} className="inline mx-1" />{" "}
              </>
            )}
            <span className="text-primary font-medium">{move.to_title}</span>
            <span> · </span>
            <span>{move.to_organization}</span>
          </div>

          {move.announcement && (
            <p className="mt-3 text-sm text-foreground leading-relaxed">&ldquo;{move.announcement}&rdquo;</p>
          )}

          <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
            <span>{formatRelative(move.effective_date)}</span>
            <span className="inline-flex items-center gap-1"><Heart size={12} /> {move.endorsements ?? 0}</span>
          </div>
        </div>
      </div>
    </article>
  );
}

function initials(name) {
  if (!name) return "?";
  return name
    .replace(/^Dr\.?\s+/i, "")
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function formatRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const days = Math.floor(diffMs / 86400000);
  if (days < 1) return "Today";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)} mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}
